<template>
  <BackofficeLayout
    :title="$t('message.registration_form.application_form')"
    :breadcrumb="breadcrumb"
    :loading="loading"
  >
    <div class="wrapper">
      <div class="main">
        <v-sheet
          class="px-5 py-5 radius-10px"
          color="white"
          rounded
          elevation="1"
        >
          <v-row no-gutters>
            <v-col cols="3">
              <p class="form-title">
                {{ $t("message.registration_form.application_detail") }}
              </p>
            </v-col>
            <v-col cols="9">
              <v-row no-gutters>
                <v-col col="4">
                  <div class="form-meta">
                    <label>{{ $t("message.common.academic_year") }}</label>
                    <p>
                      {{ registrationForm?.semester_year ?? "-" }}
                    </p>
                  </div>
                </v-col>
                <v-col col="4">
                  <div class="form-meta">
                    <label>{{
                      $t("message.registration_form.application_title")
                    }}</label>
                    <p>
                      {{ registrationForm?.register_name ?? "-" }}
                    </p>
                  </div>
                </v-col>
                <v-col col="4">
                  <div class="form-meta">
                    <label>{{ $t("message.common.status") }}</label>
                    <p>
                      {{ formStatusText(registrationForm?.form_status ?? "-") }}
                    </p>
                  </div>
                </v-col>
              </v-row>
              <!-- opening date -->
              <v-row no-gutters>
                <v-col col="4">
                  <div class="form-meta">
                    <label>{{
                      $t("message.registration_form.application_opening_date")
                    }}</label>
                    <p>
                      {{
                        formatDateTimeLocale(
                          registrationForm?.opening_date ?? null
                        )
                      }}
                    </p>
                  </div>
                </v-col>
                <v-col col="4">
                  <div class="form-meta">
                    <label>{{ $t("message.common.time") }}</label>
                    <p>
                      {{
                        formatTimeLocale(
                          registrationForm?.opening_time ??
                            null
                        )
                      }}
                    </p>
                  </div>
                </v-col>
                <v-col col="4"> </v-col>
              </v-row>
              <!-- closing date -->
              <v-row no-gutters>
                <v-col col="4">
                  <div class="form-meta">
                    <label>{{
                      $t("message.registration_form.application_closing_date")
                    }}</label>
                    <p>
                      {{
                        formatDateTimeLocale(
                          registrationForm?.closing_date ?? null
                        )
                      }}
                    </p>
                  </div>
                </v-col>
                <v-col col="4">
                  <div class="form-meta">
                    <label>{{ $t("message.common.time") }}</label>
                    <p>
                      {{
                        formatTimeLocale(
                          registrationForm?.closing_time ??
                            null
                        )
                      }}
                    </p>
                  </div>
                </v-col>
                <v-col col="4"> </v-col>
              </v-row>
              <!-- application fee -->
              <v-row no-gutters>
                <v-col col="4">
                  <div class="form-meta">
                    <label>{{
                      $t("message.registration_form.application_fee")
                    }}</label>
                    <p>
                      {{ formatFee(registrationForm?.fee ?? null) }}
                    </p>
                  </div>
                </v-col>
                <v-col col="4">
                  <div class="form-meta">
                    <label>{{
                      $t("message.registration_form.deadline_of_payment")
                    }}</label>
                    <p>
                      {{
                        formatDateTimeLocale(
                          registrationForm?.payment_end_date ?? null
                        )
                      }}
                    </p>
                  </div>
                </v-col>
                <v-col col="4">
                  <div class="form-meta">
                    <label>{{
                      $t("message.registration_form.application_link")
                    }}</label>
                    <a
                      class="truncate d-flex align-baseline link--disabled"
                      v-if="
                        registrationForm &&
                        (registrationForm.form_status === 'DEACTIVE' ||
                          registrationForm.form_status === 'DRAFT')
                      "
                    >
                      <span class="link--disabled">
                        {{
                          formatLink(registrationForm?.register_link ?? null)
                        }}
                      </span>
                      <v-icon
                        v-if="registrationForm?.register_link ?? false"
                        class="icon--disabled"
                        right
                        small
                        >mdi-content-copy</v-icon
                      >
                    </a>
                    <a
                      v-else
                      class="truncate d-flex align-baseline"
                      @click="copyToClipboard"
                    >
                      <span>
                        {{
                          formatLink(registrationForm?.register_link ?? null)
                        }}
                      </span>
                      <v-icon
                        v-if="registrationForm?.register_link ?? false"
                        color="primary"
                        right
                        small
                        >mdi-content-copy</v-icon
                      >
                    </a>
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-sheet>
        <v-sheet
          class="px-5 py-5 radius-10px"
          color="white"
          rounded
          elevation="1"
        >
          <v-row no-gutters>
            <v-col cols="3">
              <p class="form-title">
                {{ $t("message.registration_form.documents_for_application") }}
              </p>
            </v-col>
            <v-col cols="7">
              <v-row no-gutters>
                <v-col col="4">
                  <div class="form-meta">
                    <label>{{ $t("message.common.documents") }}</label>
                    <div class="form-description form-description--text-large">
                      <ul>
                        <li
                          v-if="
                            registrationForm?.has_document_national_id ?? false
                          "
                          class="text-black"
                        >
                          {{ $t("message.registration_form.copy_of_id_card") }}
                        </li>
                        <li
                          v-if="
                            registrationForm?.has_document_house_registration ??
                            false
                          "
                          class="text-black"
                        >
                          {{
                            $t(
                              "message.registration_form.copy_of_home_registration"
                            )
                          }}
                        </li>
                        <li
                          v-if="
                            registrationForm?.has_document_proof_of_birth ??
                            false
                          "
                          class="text-black"
                        >
                          {{
                            $t(
                              "message.registration_form.copy_of_birth_certificate"
                            )
                          }}
                        </li>
                        <li
                          v-if="
                            registrationForm?.has_document_proof_of_name_changed ??
                            false
                          "
                          class="text-black"
                        >
                          {{
                            $t("message.registration_form.copy_of_name_change")
                          }}
                        </li>
                        <li
                          v-if="
                            registrationForm?.has_document_proof_of_adopted ??
                            false
                          "
                          class="text-black"
                        >
                          {{
                            $t(
                              "message.registration_form.copy_of_legitimation_of_child"
                            )
                          }}
                        </li>
                        <li
                          v-if="
                            registrationForm?.has_document_medical_certificate ??
                            false
                          "
                          class="text-black"
                        >
                          {{
                            $t("message.registration_form.medical_certificate")
                          }}
                        </li>
                        <li
                          v-if="
                            registrationForm?.has_document_transcript ?? false
                          "
                          class="text-black"
                        >
                          {{
                            $t("message.registration_form.copy_of_transcript")
                          }}
                        </li>
                        <li
                          v-if="
                            registrationForm?.has_document_portfolio ?? false
                          "
                          class="text-black"
                        >
                          {{ $t("message.registration_form.portfolio") }}
                        </li>
                        <li
                          :key="`document-setting-${item.id}`"
                          v-for="item in registrationForm?.register_documents_setting ??
                          []"
                          class="text-black"
                        >
                          {{ item.document_name }}
                        </li>
                      </ul>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-col>
            <v-col class="d-flex justify-end" cols="2">
              <img width="50" src="../../../assets/file.svg" />
            </v-col>
          </v-row>
        </v-sheet>
        <v-sheet
          class="px-5 py-5 radius-10px"
          color="white"
          rounded
          elevation="1"
        >
          <v-row class="flex-column row-column" no-gutters>
            <v-col>
              <v-row no-gutters>
                <v-col cols="3">
                  <p class="form-title">
                    {{
                      $t(
                        "message.registration_form.announcement_of_qualify_candidates"
                      )
                    }}
                  </p>
                </v-col>
                <v-col cols="9">
                  <v-row no-gutters>
                    <v-col col="4">
                      <div class="form-meta">
                        <label>{{
                          $t("message.common.announcement_date")
                        }}</label>
                        <p>
                          {{
                            formatDateTimeLocale(
                              registrationForm?.exam_authorize_announcement_date ??
                                null
                            )
                          }}
                        </p>
                      </div>
                    </v-col>
                    <v-col col="4">
                      <div class="form-meta">
                        <label>{{ $t("message.common.time") }}</label>
                        <p>
                          {{
                            formatTimeLocale(
                              registrationForm?.exam_authorize_announcement_time ??
                                null
                            )
                          }}
                        </p>
                      </div>
                    </v-col>
                    <v-col col="4"> </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-col>
            <v-col>
              <v-row no-gutters>
                <v-col cols="3">
                  <p class="form-title">
                    {{ $t("message.registration_form.exam_date") }}
                  </p>
                </v-col>
                <v-col cols="9">
                  <v-row no-gutters>
                    <v-col col="4">
                      <div class="form-meta">
                        <label>{{
                          $t("message.registration_form.exam_date")
                        }}</label>
                        <p>
                          {{
                            formatDateTimeLocale(
                              registrationForm?.exam_date ?? null
                            )
                          }}
                        </p>
                      </div>
                    </v-col>
                    <v-col col="4">
                      <div class="form-meta">
                        <label>{{
                          $t("message.registration_form.exam_period")
                        }}</label>
                        <p>
                          {{
                            formatTimeLocale(
                              registrationForm?.exam_start_time_phase1 ?? null
                            )
                          }}
                        </p>
                      </div>
                    </v-col>
                    <v-col col="4"> </v-col>

                    <!-- <v-col col="4">
                      <div class="form-meta">
                        <label>{{
                          $t("message.registration_form.test_venue")
                        }}</label>
                        <p>
                          {{ registrationForm?.exam_location ?? "-" }}
                        </p>
                      </div>
                    </v-col>
                    <v-col col="4">
                      <div class="form-meta">
                        <label>{{
                          $t("message.registration_form.exam_date")
                        }}</label>
                        <p>
                          {{
                            formatDateTimeLocale(
                              registrationForm?.exam_date ?? null
                            )
                          }}
                        </p>
                      </div>
                    </v-col>
                    <v-col col="4"> </v-col>
                  </v-row>
                  <v-row no-gutters>
                    <v-col col="4">
                      <div class="form-meta">
                        <label>{{
                          $t("message.registration_form.first_exam_period")
                        }}</label>
                        <p>
                          {{
                            formatDateTimeRange(
                              registrationForm?.exam_start_time_phase1 ?? null,
                              registrationForm?.exam_end_time_phase1 ?? null
                            )
                          }}
                        </p>
                      </div>
                    </v-col>
                    <v-col col="4">
                      <div class="form-meta">
                        <label>{{
                          $t("message.registration_form.second_exam_period")
                        }}</label>
                        <p>
                          {{
                            formatDateTimeRange(
                              registrationForm?.exam_start_time_phase2 ?? null,
                              registrationForm?.exam_end_time_phase2 ?? null
                            )
                          }}
                        </p>
                      </div>
                    </v-col>
                    <v-col col="4"> </v-col> -->
                  </v-row>
                </v-col>
              </v-row>
            </v-col>
            <v-col>
              <v-row no-gutters>
                <v-col cols="3">
                  <p class="form-title">
                    {{
                      $t(
                        "message.registration_form.announcement_of_examination_result"
                      )
                    }}<br />({{ $t("message.registration_form.first_round") }})
                  </p>
                </v-col>
                <v-col cols="9">
                  <v-row no-gutters>
                    <v-col col="4">
                      <div class="form-meta">
                        <label>{{
                          $t("message.common.announcement_date")
                        }}</label>
                        <p>
                          {{
                            formatDateTimeLocale(
                              registrationForm?.announcement_exam_result_date ??
                                null
                            )
                          }}
                        </p>
                      </div>
                    </v-col>
                    <v-col col="4">
                      <div class="form-meta">
                        <label>{{ $t("message.common.time") }}</label>
                        <p>
                          {{
                            formatTimeLocale(
                              registrationForm?.announcement_exam_result_time ??
                                null
                            )
                          }}
                        </p>
                      </div>
                    </v-col>
                    <v-col col="4">
                      <div class="form-meta">
                        <label>{{
                          $t(
                            "message.registration_form.deadline_of_confirmation"
                          )
                        }}</label>
                        <p>
                          {{
                            formatDateTimeLocale(
                              registrationForm?.announcement_exam_result_deadline_date ??
                                null
                            )
                          }}
                        </p>
                      </div>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-col>
            <v-col>
              <v-row no-gutters>
                <v-col cols="3">
                  <p class="form-title">
                    {{
                      $t(
                        "message.registration_form.announcement_of_examination_result"
                      )
                    }}<br />({{ $t("message.registration_form.reserve_round") }})
                  </p>
                </v-col>
                <v-col cols="9">
                  <v-row no-gutters>
                    <v-col col="4">
                      <div class="form-meta">
                        <label>{{
                          $t("message.common.announcement_date")
                        }}</label>
                        <p>
                          {{
                            formatDateTimeLocale(
                              registrationForm?.reserved_announcement_exam_result_date ??
                                null
                            )
                          }}
                        </p>
                      </div>
                    </v-col>
                    <v-col col="4">
                      <div class="form-meta">
                        <label>{{ $t("message.common.time") }}</label>
                        <p>
                          {{
                            formatTimeLocale(
                              registrationForm?.reserved_announcement_exam_result_time ??
                                null
                            )
                          }}
                        </p>
                      </div>
                    </v-col>
                    <v-col col="4">
                      <div class="form-meta">
                        <label>{{
                          $t(
                            "message.registration_form.deadline_of_confirmation"
                          )
                        }}</label>
                        <p>
                          {{
                            formatDateTimeLocale(
                              registrationForm?.reserved_announcement_exam_result_deadline_date ??
                                null
                            )
                          }}
                        </p>
                      </div>
                    </v-col>
                  </v-row>
                  <v-row no-gutters>
                    <v-col col="12">
                      <p class="form-remark py-4">
                        <span style="color: red">*</span>
                        {{
                          $t(
                            "message.registration_form.application_confirmation_notify"
                          )
                        }}
                      </p>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-col>
            <v-col>
              <v-row no-gutters>
                <v-col cols="3">
                  <p class="form-title">
                    {{ $t("message.registration_form.the_initiation_day") }}
                  </p>
                </v-col>
                <v-col cols="9">
                  <v-row no-gutters>
                    <v-col cols="4">
                      <div class="form-meta">
                        <label>{{ $t("message.common.date") }}</label>
                        <p>
                          {{
                            formatDateTimeLocale(
                              registrationForm?.enrollment_date ?? null
                            )
                          }}
                        </p>
                      </div>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-sheet>
        <v-sheet color="white" elevation="1" class="radius-10px">
          <v-sheet class="px-5 py-5 radius-10px" color="white" elevation="0">
            <v-row class="flex-column row-column" no-gutters>
              <p class="form-title">
                {{ $t("message.registration_form.curriculum_and_grade_level") }}
              </p>
              <div class="form-row form-row-gap--xl">
                <div class="form-row form-row-gap--sm mb-0">
                  <v-text-field
                    v-model="keyword"
                    outlined
                    :placeholder="
                      $t('message.registration_form.search_by_curriculum')
                    "
                    prepend-inner-icon="mdi-magnify"
                    required
                    hide-details
                    dense
                    style="width: 400px"
                  ></v-text-field>
                  <v-btn
                    v-if="keyword.length > 0"
                    color="primary"
                    outlined
                    elevation="0"
                    width="90"
                    @click="search"
                  >
                    {{ $t("message.common.search") }}
                  </v-btn>
                  <v-btn
                    v-else
                    outlined
                    elevation="0"
                    width="90"
                    @click="search"
                  >
                    {{ $t("message.common.search") }}
                  </v-btn>
                </div>
                <!-- <v-select
                  v-model="selectedStudentYearValue"
                  @change="changeStudentYear"
                  outlined
                  :items="studentYear"
                  :item-text="`label_${$i18n.locale}`"
                  item-value="value"
                  prepend-inner-icon="mdi-filter-variant"
                  hide-details
                  dense
                  style="width: 240px"
                ></v-select> -->
              </div>
            </v-row>
          </v-sheet>
          <v-data-table
            class="custom-data-table custom-data-table--color"
            :headers="headers"
            :items="registrationPlan"
            hide-default-header
            hide-default-footer
          >
            <template v-slot:header="{ props: { headers } }">
              <thead>
                <tr>
                  <th
                    v-for="item in headers"
                    :key="item.value"
                    class="column px-3"
                    :style="{ width: `${item.width}` }"
                  >
                    <div
                      v-if="item.sortable"
                      class="custom-table-header"
                      @click="handleChangeSort(item.sort)"
                    >
                      {{ item.text }}
                      <template v-if="item.sort === sort">
                        <v-icon v-if="sortDirection === 'desc'" small
                          >mdi-chevron-up</v-icon
                        >
                        <v-icon v-else small>mdi-chevron-down</v-icon>
                      </template>
                      <template v-else>
                        <v-icon small>mdi-unfold-more-horizontal</v-icon>
                      </template>
                    </div>
                    <div v-else>
                      {{ item.text }}
                    </div>
                  </th>
                </tr>
              </thead>
            </template>
            <template v-slot:[`item.student_year_name_th`]="{ item }">
              {{ item.student_year[`student_year_name_${$i18n.locale}`] }}
            </template>
            <template v-slot:[`item.program_name_th`]="{ item }">
              {{ item.program[`program_name_${$i18n.locale}`] }}
            </template>
            <template v-slot:no-data>
              <div class="custom-data-table-empty-data">
                <div>
                  <v-icon class="mb-2" large>mdi-plus-box</v-icon>
                  <p>
                    {{
                      $t(
                        "message.registration_form.curriculum_available_on_application"
                      )
                    }}
                  </p>
                </div>
              </div>
            </template>
          </v-data-table>
          <v-sheet
            v-if="registrationPlan.length > 0"
            class="py-5 px-5 radius-10px"
            elevation="0"
            style="border-top: 1px solid #e4e7f2"
          >
            <div class="form-row form-row--end">
              <div class="form-meta form-meta--right">
                <p>
                  {{
                    $t("message.registration_form.total_required_amount", {
                      total: totalNumberOfStudentPlan,
                    })
                  }}
                </p>
              </div>
            </div>
          </v-sheet>
        </v-sheet>
      </div>
    </div>
  </BackofficeLayout>
</template>

<script>
import BackofficeLayout from "@/components/common/BackofficeLayout";
import {
  FORM_STATUS_MAPPING,
  // REGISTRATION_DOCUMENTS,
  OPPOSITE_SORT,
} from "@/constants";
import * as services from "@/services";
import * as dateUtils from "@/utils/date";
import router from "@/router";

const DEFAULT_SORT_KEY = "student_year.student_year_name_th";
const DEFAULT_SORT_DIRECTION = "ASC";
const DEFAULT_STUDENT_YEAR_VALUE = 0;

export default {
  name: "RegistrationForm",
  components: {
    BackofficeLayout,
  },
  data() {
    return {
      registrationForm: null,
      registrationPlan: [],
      // registrationDocumentsOptions: REGISTRATION_DOCUMENTS,
      program: [],
      studentYear: [],
      keyword: "",
      selectedStudentYearValue: DEFAULT_STUDENT_YEAR_VALUE,
      sort: DEFAULT_SORT_KEY,
      sortDirection: DEFAULT_SORT_DIRECTION,
      loading: 0,
    };
  },

  mounted() {
    this.getResource();
    this.getRegistrationFormById();
    this.init();
  },
  methods: {
    init() {
      const { query } = this.$route;
      const keyword = query.keyword || "";
      const studentYearValue = query.student_year
        ? parseInt(query.student_year)
        : DEFAULT_STUDENT_YEAR_VALUE;
      const sort = query.sort ? query.sort.split(":") : "";
      const sortKey = sort?.[0] ?? DEFAULT_SORT_KEY;
      const sortDirection = sort?.[1] ?? DEFAULT_SORT_DIRECTION;
      this.keyword = keyword;
      this.selectedStudentYearValue = studentYearValue;
      this.sort = sortKey;
      this.sortDirection = sortDirection;
      this.getRegistrationPlan();
    },
    async getResource() {
      try {
        this.loading++;
        const {
          data: { data },
        } = await services.getStudentYear();

        this.studentYear = [
          ...data.student_year.map((item) => ({
            value: item.value,
            label_th: item.student_year_name_th,
            label_en: item.student_year_name_en,
          })),
        ];
      } catch (error) {
        console.log(error);
      } finally {
        this.loading--;
      }
    },
    async getRegistrationFormById() {
      try {
        this.loading++;
        const id = this.$route.params?.id;
        const {
          data: { data },
        } = await services.getRegistrationFormById(id);
        this.registrationForm = data.registration_form;
      } catch (error) {
        console.log(error);
      } finally {
        this.loading--;
      }
    },
    async getRegistrationPlan() {
      try {
        this.loading++;
        const params = this.prepareQuery();
        const {
          data: { data },
        } = await services.getRegistrationPlan(params);
        this.registrationPlan = data.registration_plan;
      } catch (error) {
        console.log(error);
      } finally {
        this.loading--;
      }
    },
    copyToClipboard() {
      navigator.clipboard.writeText(
        `${window.location.origin}${this.registrationForm.register_link}`
      );
    },
    search() {
      const path = this.$route.path;
      const query = this.prepareQuery();
      router.push({
        path,
        query: { ...query, keyword: this.keyword },
      });
    },
    changeStudentYear(value) {
      const path = this.$route.path;
      const query = this.prepareQuery();
      router.push({
        path,
        query: { ...query, student_year: value },
      });
    },
    handleChangeSort(key) {
      let direction = "desc";
      if (key === this.sort) {
        direction = OPPOSITE_SORT[this.sortDirection] || "desc";
      }
      const sort = `${key}:${direction}`;
      const path = this.$route.path;
      const query = this.prepareQuery();
      router.push({
        path,
        query: { ...query, sort },
      });
    },
    prepareQuery() {
      const id = this.$route.params?.id;
      return {
        registration_form_id: id,
        keyword: this.keyword,
        student_year: this.selectedStudentYearValue,
        sort: `${this.sort}:${this.sortDirection}`,
      };
    },
    formStatusText(status) {
      if (!status) return "";
      return FORM_STATUS_MAPPING?.[status]?.[`text_${this.$i18n.locale}`];
    },
    formatDateTimeLocale(date) {
      if (!date) return "-";
      return dateUtils.formatDateTime(date, this.$i18n.locale);
    },
    formatTimeLocale(time) {
      if (!time) return "-";
      const suffix = this.$i18n.locale === "th" ? " น." : "";
      return `${time}${suffix}`;
    },
    formatFee(fee) {
      if (!fee) return "-";
      return `${this.$options.filters.currency(fee)} ${this.$t(
        "message.common.currency.baht"
      )}`;
    },
    formatLink(link) {
      if (!link) return "-";
      return `${window.location.origin}${this.link}`;
    },
    formatDateTimeRange(start, end) {
      const suffix = this.$i18n.locale === "th" ? " น." : "";
      if (!start && !end) return "-";
      return `${start} - ${end} ${suffix}`;
    },
  },
  computed: {
    totalNumberOfStudentPlan() {
      return this.registrationPlan
        .map((item) => parseInt(item.total_students))
        .reduce((previous, current) => previous + current, 0);
    },
    breadcrumb() {
      return [
        {
          text: this.$t("message.registration_form.set_up_application_form"),
          link: "/registration/form",
        },
        {
          text: this.$t("message.registration_form.view_the_application_form"),
        },
      ];
    },
    headers() {
      return [
        {
          text: this.$t("message.common.grade_level"),
          value: "student_year_name_th",
          sortable: true,
          sort: "student_year.student_year_name_th",
          width: "40%",
        },
        {
          text: this.$t("message.common.curriculum"),
          value: "program_name_th",
          sortable: true,
          sort: "program.program_name_th",
          width: "40%",
        },
        {
          text: this.$t("message.registration_form.required_amount"),
          value: "total_students",
          sortable: true,
          sort: "register_plan.total_students",
          width: "20%",
        },
      ];
    },
  },
  watch: {
    $route() {
      this.init();
    },
  },
};
</script>

<style scoped>
.wrapper {
  padding: 0 24px 24px 24px;
}
.main {
  display: grid;
  grid-template-columns: 1fr;
  align-items: flex-start;
  gap: 24px;
  padding-top: 24px;
  border-top: 1px solid #d7d6d8;
}
.panel {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.truncate > span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 200px;
}

.radius-10px {
  border-radius: 10px !important;
}
</style>
